import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import type { Tone } from '../lib/labels'
import { FOCUS_RING } from './control-styles'
import { IconClose } from './icons'

/*
 * Each tone is one Untitled family, read through the family's own soft fill,
 * soft border and text step, so a tone is three tokens and never a literal.
 * The colours carry meaning and are copied exactly: a campaign contact that is
 * `Active` is blue, not green, and a sender profile `Draft` is amber where a
 * campaign `Draft` is gray.
 */
const TONES: Record<Tone, string> = {
  neutral: 'bg-neutral-soft text-neutral-text border-neutral-soft-border',
  info: 'bg-info-soft text-info-text border-info-soft-border',
  success: 'bg-success-soft text-success-text border-success-soft-border',
  warning: 'bg-warning-soft text-warning-text border-warning-soft-border',
  danger: 'bg-danger-soft text-danger-text border-danger-soft-border',
  accent: 'bg-accent-soft text-accent-text border-accent-soft-border',
}

const DOT_TONES: Record<Tone, string> = {
  neutral: 'bg-neutral',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  accent: 'bg-accent',
}

/** 22 tall in 12/18, or 24 tall in 14/20. Always a full pill with a hairline. */
const BADGE_SIZES: Record<'sm' | 'md', string> = {
  sm: 'h-[22px] px-2 text-xs',
  md: 'h-6 px-2.5 text-base',
}

export interface BadgeProps {
  children: ReactNode
  tone?: Tone
  /** A 6px dot in the family's base colour, before the label. */
  dot?: boolean
  size?: 'sm' | 'md'
  /** A 12px glyph before the label. */
  icon?: ReactNode
  className?: string
  title?: string
}

/** A state label. Read-only, never clickable. */
export function Badge({ children, tone = 'neutral', dot, size = 'sm', icon, className, title }: BadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex max-w-full shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border font-medium',
        BADGE_SIZES[size],
        TONES[tone],
        className,
      )}
    >
      {dot ? <span aria-hidden="true" className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOT_TONES[tone])} /> : null}
      {icon ? <span className="inline-flex shrink-0 [&>svg]:h-3 [&>svg]:w-3">{icon}</span> : null}
      <span className="truncate">{children}</span>
    </span>
  )
}

/** The two colourings a tag chip comes in. */
export type TagTone = 'accent' | 'neutral'

const TAG_TONES: Record<TagTone, string> = {
  accent: 'border-primary-200 bg-primary-50 text-primary-700',
  neutral: 'border-neutral-soft-border bg-neutral-soft text-neutral-text',
}

const TAG_REMOVE: Record<TagTone, string> = {
  accent: 'text-primary-400 hover:text-primary-600',
  neutral: 'text-ink-faint hover:text-ink-subtle',
}

export interface TagProps {
  children: ReactNode
  /** The full name, for the tooltip and the remove button's name. Defaults to the text of `children`. */
  name?: string
  tone?: TagTone
  /** Truncates past this width, in pixels, with the full name in a tooltip. */
  maxWidth?: number
  onRemove?: () => void
  className?: string
}

/**
 * A label somebody put on a contact: 22 tall, a 6px corner, the brand tint.
 *
 * Squarer than a badge on purpose. A badge says what state a thing is in and
 * is never pressed, a tag is a thing a person attached and can take off, and
 * the corner is how the two stay apart at a glance in a dense row.
 */
export function Tag({ children, name, tone = 'accent', maxWidth = 160, onRemove, className }: TagProps) {
  const full = name ?? (typeof children === 'string' || typeof children === 'number' ? String(children) : undefined)
  return (
    <span
      title={full}
      style={{ maxWidth }}
      className={cn(
        'inline-flex h-[22px] shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-1.5 text-xs font-medium',
        TAG_TONES[tone],
        className,
      )}
    >
      <span className="min-w-0 truncate">{children}</span>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove tag ${full ?? ''}`.trim()}
          className={cn('-mr-0.5 inline-flex shrink-0 rounded-sm transition', TAG_REMOVE[tone], FOCUS_RING)}
        >
          <IconClose aria-hidden="true" className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  )
}

/** A coloured dot on its own, for dense table cells where a badge is too heavy. */
export function StatusDot({ tone = 'neutral', label, className }: { tone?: Tone; label: string; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <span className={cn('h-2 w-2 shrink-0 rounded-full', DOT_TONES[tone])} aria-hidden="true" />
      <span className="text-base text-ink-muted">{label}</span>
    </span>
  )
}

export interface PillProps {
  children: ReactNode
  tone?: Tone
  /** Turns the pill into a toggle, for a filter chip. */
  onClick?: () => void
  active?: boolean
  /** Adds a remove button. The pill announces what it removes. */
  onRemove?: () => void
  removeLabel?: string
  className?: string
}

/** An interactive chip, used for filters and multi-select values. */
export function Pill({
  children,
  tone = 'neutral',
  onClick,
  active,
  onRemove,
  removeLabel,
  className,
}: PillProps) {
  const shared = cn(
    'inline-flex h-6 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition',
    active ? 'border-primary-300 bg-accent-soft text-accent-text' : TONES[tone],
    className,
  )

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={cn(shared, 'hover:border-line-strong', FOCUS_RING)}
      >
        {children}
      </button>
    )
  }

  return (
    <span className={shared}>
      {children}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="-mr-1 rounded-full p-0.5 text-current opacity-60 transition hover:bg-black/5 hover:opacity-100"
          aria-label={removeLabel ?? 'Remove'}
        >
          <IconClose className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  )
}

/** A monospace token for ids and short codes, so they never get mistaken for prose. */
export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('font-mono text-xs text-ink-muted', className)}>{children}</span>
  )
}
